import { IStoredCalculationSource, type ICurrentDayResponse, type ILog } from "../Types"
import { normalizeCurrentDay } from "./CurrentDay"
import type { DataStore } from "./Store"
import { calculationProjectionFields } from "./Connections/Folds"

type Mutation = NonNullable<Parameters<DataStore["applyBundle"]>[2]>[number];

// current.day is not a connection fetch. Give this request the same request-local
// live replay as fetchQuery, without opening any additional socket subscriptions.
// Permanent applyBundlePreservingLive ownership is unsuitable here: reconnect must
// accept offline corrections even for records that were live before disconnect.
export function beginCurrentDayLoad(store: DataStore) {
  const live: Mutation[] = [];
  const types = ["Cruise", "Trip", "Day", "Note", "NoteAttachment", "StoredCalculation", "Log", "Status"] as const;
  const releases = types.map((type) => store.subscribeLiveEvent(type, (row) => {
    const current = store.getEntity(type, row.id);
    live.push({ type, entity: current ?? row, deleted: current === undefined });
  }));
  const currentLog = store.getCurrentLog();
  return {
    dispose: () => releases.forEach((release) => release()),
    apply: (response: ICurrentDayResponse, provisionalLogs: ILog[]) => {
      const normalized = normalizeCurrentDay(response);
      const entities = normalized.bundle.entities;
      const cruises = new Set(entities.Cruise?.map((row) => row.id));
      const trips = new Set(entities.Trip?.map((row) => row.id));
      const days = new Set(entities.Day?.map((row) => row.id));
      const notes = new Set(entities.Note?.map((row) => row.id));
      const attachments = new Set(entities.NoteAttachment?.map((row) => row.id));
      const removals: Mutation[] = [];
      // current.day returns complete status lists for its returned days. A reconnect
      // must remove offline deletions too; request-local live events replay below.
      if (response.status !== undefined) {
        const statuses = new Set(entities.Status?.map((row) => row.id));
        for (const day of entities.Day ?? []) {
          for (const row of store.getQuery("Status", { shipId: day.shipId, dayId: day.id })) {
            if (!statuses.has(row.id)) removals.push({ type: "Status", entity: row, deleted: true });
          }
        }
      }
      // A fresh aggregate projection supersedes old folded calculations. Real live
      // rows racing this request are replayed afterward with their original identity.
      for (const [sourceType, ids] of [
        [IStoredCalculationSource.cruise, cruises],
        [IStoredCalculationSource.trip, trips],
        [IStoredCalculationSource.day, days],
      ] as const) {
        for (const sourceId of ids) {
          for (const row of store.getQuery("StoredCalculation", { sourceId })) {
            if (row.sourceType === sourceType) removals.push({ type: "StoredCalculation", entity: row, deleted: true });
          }
        }
      }
      const ownedNotes = new Set(notes);
      const cachedNotes = new Map([
        ...[...cruises].flatMap((cruiseId) => store.getQuery("Note", { cruiseId })),
        ...[...trips].flatMap((tripId) => store.getQuery("Note", { tripId })),
        ...[...days].flatMap((dayId) => store.getQuery("Note", { dayId })),
      ].map((row) => [row.id, row]));
      for (const row of cachedNotes.values()) {
        const owned = row.dayId !== undefined ? days.has(row.dayId)
          : row.tripId !== undefined ? trips.has(row.tripId)
            : row.cruiseId !== undefined && cruises.has(row.cruiseId);
        if (owned) {
          ownedNotes.add(row.id);
          if (!notes.has(row.id)) removals.push({ type: "Note", entity: row, deleted: true });
        }
      }
      for (const noteId of ownedNotes) {
        for (const row of store.getQuery("NoteAttachment", { noteId })) {
          if (!attachments.has(row.id)) removals.push({ type: "NoteAttachment", entity: row, deleted: true });
        }
      }
      const replay = live.map((mutation): Mutation => {
        if (mutation.deleted || !["Cruise", "Trip", "Day"].includes(mutation.type)) return mutation;
        // Base-row edits carry no newly computed projection. Their preserveLive
        // fields may predate this request, so use the response's projection here;
        // any genuinely newer calculation has its own replayed StoredCalculation.
        const snapshot = entities[mutation.type]?.find((row) => row.id === mutation.entity.id);
        if (!snapshot) return mutation;
        const row = { ...mutation.entity } as { id: number } & Record<string, unknown>;
        for (const field of calculationProjectionFields) {
          delete row[field];
          if (snapshot && field in snapshot) row[field] = snapshot[field as keyof typeof snapshot];
        }
        return { ...mutation, entity: row };
      });
      store.applyBundle(normalized.bundle, undefined, [...removals, ...replay]);
      if (store.getCurrentLog() === currentLog && normalized.currentLog) {
        store.applyCurrentLog(normalized.currentLog, "update");
      }
      store.replaceProvisionalLogs(provisionalLogs);
    },
  };
}
